import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Platform } from "@ionic/angular"; // Updated for Ionic 4+
import { BehaviorSubject, Observable, OperatorFunction, concat } from "rxjs"; // Updated for RxJS 6+
// import { Network } from '@awesome-cordova-plugins/network/ngx'; // Updated for the latest Ionic Native plugins
// import { Device } from '@awesome-cordova-plugins/device/ngx'; // Updated for the latest Ionic Native plugins
import { type ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, map, shareReplay } from "rxjs/operators";

import { environment } from "../environments/environment";

import { routes as APP_ROUTES } from "./app-routing.module";

import * as packageConfig from "../../package.json";

import { console } from "#/log";
import { AlertService } from "./core/alerts/alerts.service";
import {
  SwUpdate,
  VersionEvent,
  VersionInstallationFailedEvent,
  VersionReadyEvent,
} from "@angular/service-worker";
import { LoadingService } from "./core/loading/loading.service";
import { Title } from "@angular/platform-browser";
import { SAMPLES, SAMPLE_KEYS } from "./core/data/samples.data";

/**
 * Service to add classes to app wrapper
 * TODO: refactor  app-wide services (analytics and page title) logic to be
 * handled here.
 */
@Injectable()
export class AppService {
  public static readonly packageConfig = packageConfig;

  public appActive: BehaviorSubject<boolean> = new BehaviorSubject(true);

  private renderer: Renderer2;
  public readonly pwaUpdateReady: Observable<VersionReadyEvent | null>;

  public static buildEnv(): "prod" | "dev" {
    if (environment.production === true) {
      return "prod";
    }
    return "dev";
  }

  public static isDevBuild(): boolean {
    return AppService.buildEnv() === "dev";
  }

  public static showDevUi(): boolean {
    return Boolean(environment.showDevUi);
  }
  public showDevUi(): boolean {
    return AppService.showDevUi();
  }

  //helper function - safely handles circular references
  static safeStringify = (obj: any, indent = 2) => {
    let cache: any[] = [];
    const retVal = JSON.stringify(
      obj,
      (_, value) =>
        typeof value === "object" && value !== null
          ? cache.indexOf(value) >= 0
            ? undefined // Duplicate reference found, discard key
            : cache.push(value) && value // Store value in our collection
          : value,
      indent,
    );
    cache = [];
    return retVal;
  };

  constructor(
    private platform: Platform,
    // private network: Network,
    // private device: Device,
    private router: Router, // Inject Router
    rendererFactory: RendererFactory2, // Inject RendererFactory2
    protected alertService: AlertService,
    protected sw: SwUpdate,
    protected loading: LoadingService,
    protected title: Title,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    // Listen to navigation events
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ), // Type guard to narrow down the type
      )
      .subscribe((event: NavigationEnd) => {
        const { id, url, type } = event;
        console.debug("app-class: view entered", {
          id,
          type,
          url: url.slice(0, 40),
        });
        // Custom logic based on route change
        this.handleViewEnter(event);
      });

    // Handle back button press
    this.platform.backButton.subscribeWithPriority(10, () => {
      console.debug("app-class: back button pressed");
      // Custom logic when back button is pressed
    });

    this.watchAppStatus();

    /** create a filtered, replayable observable from the sw update channel that only includes version-ready events (otherwise null) */
    this.pwaUpdateReady = concat([null], this.sw.versionUpdates).pipe(
      /** We only care if there's a new version ready to go */
      filter(
        (event) =>
          event === null ||
          ["VERSION_READY", "VERSION_INSTALLATION_FAILED"].includes(event.type),
      ),
      map((event) =>
        event?.type === "VERSION_READY" ? event : null,
      ) as OperatorFunction<null | VersionEvent, null | VersionReadyEvent>,
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  /** Common navigation destinations */
  public static readonly URL_FOR_VIEW = {
    home: "/",
    login: "/login",
  } as const;

  /**
   * Jump to a common router path (homepage or login), abstracting
   * away the specific router path
   *
   * @see { @link APP_ROUTES }
   */
  public jumpToView(
    view: keyof typeof AppService.URL_FOR_VIEW,
  ): ReturnType<Router["navigateByUrl"]> {
    return this.router.navigateByUrl(AppService.URL_FOR_VIEW[view]);
  }

  private handleViewEnter(event: NavigationEnd) {
    // Example logic to add a class based on the route or component
    const wrapperClass = this.getWrapperClass(event.url); // Define your logic to get the class
    if (wrapperClass) {
      this.addAppClass(wrapperClass);
    }
  }

  private getWrapperClass(url: string): string | null {
    // Define how to determine the class based on the URL
    // if (url.includes('some-path')) {
    //   return 'some-class'; // Example class name
    // }
    return null;
  }

  /**
   * Returns if the app is running in a mobile app or not
   */
  isMobileApp() {
    return this.platform.is("android") || this.isIos(); // &&
    // !(this.platform.is('mobileweb') || this.device.platform === "browser");
  }

  /**
   * An unfortunate workaround for an Ionic 3 bug where `this.platform.is('ios')`
   * returns false for iPadOS v13+, due to an out-of-date user-agent regular
   * expression match.
   *
   * @returns true if this is an iOS device
   */
  isIos() {
    return this.platform.is("ios"); // || this.device.platform === 'iOS';
  }

  getPlatformDebug(): string {
    return JSON.stringify(
      {
        // device: {model: this.device.model, platform: this.device.platform, manufacturer: this.device.manufacturer},
        platforms: this.platform.platforms(),
        appServiceIsIos: this.isIos(),
      },
      null,
      2,
    );
  }

  /**
   * Checks if the device has a network connection using the native plugin.
   * For in-app usage only.
   */
  isDeviceOnline() {
    if (this.isMobileApp()) {
      //   if (this.network) { return this.network.type !== "none"; }
      console.warn(
        `isDeviceOnline needs to be hooked up to resolve online status for PWA service worker`,
      );
    } // else {
    return navigator.onLine;
    // }
    throw new Error("Network status plugin unavailable.");
  }

  /**
   * Sets the `isAppActive` property on the component based on if the app or
   * browser window is open.
   */
  private watchAppStatus(): void {
    if (this.isMobileApp()) {
      document.addEventListener(
        "pause",
        () => {
          this.appActive.next(false);
        },
        false,
      );
      document.addEventListener(
        "resume",
        () => {
          this.appActive.next(true);
        },
        false,
      );
    } else {
      document.addEventListener(
        "visibilitychange",
        () => {
          this.appActive.next(!document.hidden);
        },
        false,
      );
    }
  }

  /**
   * Adds a class to the root app element
   */
  private addAppClass(className: string) {
    try {
      const appRoot = document.querySelector("ion-app"); // Use document to select ion-app
      if (appRoot) {
        this.renderer.addClass(appRoot, className); // Use Renderer2 to add class
      }
    } catch (err) {
      console.debug("app-class: could not add class to app", className, err);
    }
  }

  /**
   * Removes a class from the root app element
   */
  private removeAppClass(className: string) {
    try {
      const appRoot = document.querySelector("ion-app"); // Use document to select ion-app
      if (appRoot) {
        this.renderer.removeClass(appRoot, className); // Use Renderer2 to remove class
      }
    } catch (err) {
      console.debug(
        "app-class: couldn't remove class from app",
        className,
        err,
      );
    }
  }

  public async checkSwUpdate() {
    let canUpdate = false;
    await this.loading.showSpinner("Checking for available update...");
    try {
      canUpdate = await this.sw.checkForUpdate();
    } catch (error) {
      let message = error instanceof Error ? error.message : String(error);

      await this.alertService.showAlert("update_error", [
        `Unable to check for updates. ${message}`,
      ]);
      return;
    } finally {
      await this.loading.dismissSpinner();
    }

    if (canUpdate) {
      const confirmed =
        await this.alertService.showConfirmAlert("confirm_update");

      if (confirmed) {
        document.location.reload();
      }
    } else {
      await this.alertService.showAlert("update_not_found");
    }
  }

  public get swEnabled() {
    return this.sw.isEnabled;
  }

  public get isStandaloneDisplay() {
    return (
      (this.platform.is("ios") &&
        "standalone" in window.navigator &&
        window.navigator.standalone) ||
      matchMedia("not (display-mode: browser)").matches
    );
  }

  public get canPromptCheckUpdate() {
    return this.swEnabled && this.platform.is("pwa");
  }

  public updateIsReady() {
    return this.pwaUpdateReady !== undefined;
  }

  public get canShowInstallNotice() {
    return (
      this.swEnabled && this.platform.is("ios") && !this.platform.is("pwa")
    );
  }

  public static sampleIdFromRoute(activatedRoute: ActivatedRoute) {
    return activatedRoute.snapshot.pathFromRoot.find(
      ({ params }) => "sampleId" in params,
    )?.params["sampleId"];
  }

  public pageTitle(params: {
    sampleName?: string;
    sampleId?: string;
    viewName?: string;
  }) {
    const title = this.resolvePageTitle(params);
    if (this.title.getTitle() !== title) {
      this.title.setTitle(title);
      console.debug(`app-service.pageTitle(): updated title:`, title);
    }
    console.debug(`app-service.pageTitle(): same title:`, title);
  }

  protected resolvePageTitle({
    sampleName,
    sampleId,
    viewName,
  }: {
    sampleName?: string;
    sampleId?: string;
    viewName?: string;
  }) {
    const appTitle = "Mastering Spectroscopy";
    if (!sampleName && sampleId && SAMPLE_KEYS.has(sampleId)) {
      sampleName = SAMPLES.find(({ $key }) => $key === sampleId)?.name;
    }

    if (sampleName && viewName) {
      /* "Compound Build: Banana (Mastering Spectroscopy)" */
      return `${viewName}: ${sampleName} (${appTitle})`;
    } else if (sampleName) {
      /* "Banana Sample (Mastering Spectroscopy)" */
      return `${sampleName} Sample (${appTitle})`;
    } else if (viewName) {
      /* "Login (Mastering Spectroscopy)" */
      return `${viewName} (${appTitle})`;
    }

    return appTitle;
  }
}
